/**
 * TruckPopoverManager Component
 *
 * Manages a single shared popover for all truck markers, rendering content
 * on-demand only when a truck is hovered or clicked. This replaces the
 * previous approach of creating popover components for every truck marker.
 *
 * Key performance benefits:
 * - Only ONE popover component in the DOM (vs 3000+ previously)
 * - Content rendered on-demand when user interacts
 * - Smooth transition between hover and click states
 */

import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Job } from '~hooks/useJob/models';

import { TruckHoverPopoverContent } from './TruckHoverPopoverContent';
import { TruckOnClickPopoverContent } from './TruckOnClickPopoverContent';
import { TruckLayerDatum } from './utils/transformTruckData';

/**
 * Popover display mode.
 * - 'hover': Shows abbreviated info, transitions to 'click' on click
 * - 'click': Shows full details with action buttons
 */
type PopoverMode = 'hover' | 'click';

/**
 * Props for the TruckPopoverManager component.
 */
interface TruckPopoverManagerProps {
  /** Currently hovered truck data (from DeckGL picking) */
  hoveredTruck: TruckLayerDatum | null;
  /** Currently clicked/selected truck data */
  clickedTruck: TruckLayerDatum | null;
  /** Screen position for the popover anchor (from DeckGL picking) */
  anchorPosition: { x: number; y: number } | null;
  /** Callback when the popover should close */
  onClose: () => void;
  /** Callback when hover state changes (for clearing hover on popover interaction) */
  onHoverChange?: (truck: TruckLayerDatum | null) => void;
  /**
   * Callback when user clicks inside the popover to "pin" it.
   * Used to persist the clicked truck state to the parent component.
   */
  onClickTruck?: (truck: TruckLayerDatum) => void;
  /** Callback when user wants to view route for a job */
  onViewRoute?: (job: Job) => void;
  /** Reference to the map container for positioning */
  mapContainerRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Debounce delay for hover popover close (ms).
 *
 * 150ms allows users to move their mouse from the truck icon to the popover
 * without it closing, based on typical mouse movement speeds. This delay was
 * chosen to balance two concerns:
 * - Too short (< 100ms): Users accidentally lose the popover when moving mouse to it
 * - Too long (> 250ms): Popovers feel "sticky" and linger when user moves away
 *
 * This value aligns with common UI debounce patterns and provides a smooth UX.
 */
const HOVER_CLOSE_DELAY_MS = 150;

/**
 * Offset from the anchor position for popover placement (pixels).
 *
 * -10px positions the popover just above the truck icon to prevent the popover
 * from covering the icon itself, which would interfere with hover interactions.
 * The negative value moves the popover upward relative to the anchor point.
 *
 * This offset was determined based on:
 * - Truck icon height (~20-24px including padding)
 * - Visual balance with the popover arrow/pointer
 * - Ensuring clear separation between icon and popover content
 */
const POPOVER_OFFSET_Y = -10;

/**
 * TruckPopoverManager manages a single shared popover that displays
 * either hover content or click content based on user interaction.
 *
 * Behavior:
 * 1. When user hovers a truck: Shows TruckHoverPopoverContent
 * 2. When user moves mouse away: Closes after delay (allows moving to popover)
 * 3. When user clicks a truck: Shows TruckOnClickPopoverContent
 * 4. When user clicks outside or presses escape: Closes popover
 *
 * @example
 * ```tsx
 * <TruckPopoverManager
 *   hoveredTruck={hoveredTruck}
 *   clickedTruck={clickedTruck}
 *   anchorPosition={anchorPosition}
 *   onClose={() => {
 *     setHoveredTruck(null);
 *     setClickedTruck(null);
 *   }}
 *   onViewRoute={handleViewRoute}
 * />
 * ```
 */
export const TruckPopoverManager = observer(
  ({
    hoveredTruck,
    clickedTruck,
    anchorPosition,
    onClose,
    onHoverChange,
    onClickTruck,
    onViewRoute,
    mapContainerRef,
  }: TruckPopoverManagerProps) => {
    // Track the current popover mode
    const [mode, setMode] = useState<PopoverMode>('hover');

    // Close timeout ref for hover debouncing - using ref to avoid triggering effects when set
    const closeTimeoutIdRef = useRef<NodeJS.Timeout | null>(null);

    // Determine which truck to show (clicked takes precedence over hovered)
    const activeTruck = clickedTruck ?? hoveredTruck;

    // Determine if popover should be open
    const isOpen = Boolean(activeTruck && anchorPosition);

    // Update mode when click state changes
    useEffect(() => {
      if (clickedTruck) {
        setMode('click');
      } else if (hoveredTruck) {
        setMode('hover');
      }
    }, [clickedTruck, hoveredTruck]);

    // Clear close timeout when a new truck is hovered
    useEffect(() => {
      if (hoveredTruck && closeTimeoutIdRef.current) {
        clearTimeout(closeTimeoutIdRef.current);
        closeTimeoutIdRef.current = null;
      }
    }, [hoveredTruck]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (closeTimeoutIdRef.current) {
          clearTimeout(closeTimeoutIdRef.current);
        }
      };
    }, []);

    /**
     * Handle mouse entering the popover.
     * Cancels any pending close and keeps popover open.
     */
    const handlePopoverMouseEnter = useCallback(() => {
      if (closeTimeoutIdRef.current) {
        clearTimeout(closeTimeoutIdRef.current);
        closeTimeoutIdRef.current = null;
      }
    }, []);

    /**
     * Handle mouse leaving the popover.
     * In hover mode, triggers close after delay.
     */
    const handlePopoverMouseLeave = useCallback(() => {
      if (mode === 'hover') {
        closeTimeoutIdRef.current = setTimeout(() => {
          onClose();
          onHoverChange?.(null);
          closeTimeoutIdRef.current = null;
        }, HOVER_CLOSE_DELAY_MS);
      }
    }, [mode, onClose, onHoverChange]);

    /**
     * Handle click inside the popover.
     * If in hover mode, transitions to click mode and notifies parent.
     */
    const handlePopoverClick = useCallback(() => {
      if (mode === 'hover' && hoveredTruck) {
        setMode('click');
        // Notify parent to persist clicked truck state
        onClickTruck?.(hoveredTruck);
      }
    }, [mode, hoveredTruck, onClickTruck]);

    /**
     * Handle clicking outside the popover (backdrop click).
     * Closes the popover and resets state.
     */
    const handleBackdropClick = useCallback(() => {
      setMode('hover');
      onClose();
    }, [onClose]);

    // Don't render anything if no truck to display
    if (!activeTruck || !anchorPosition) {
      return null;
    }

    // Create a virtual anchor element at the pick position
    const anchorEl = mapContainerRef?.current ?? null;

    return (
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        anchorReference="anchorPosition"
        anchorPosition={{
          top: anchorPosition.y + POPOVER_OFFSET_Y,
          left: anchorPosition.x,
        }}
        onClose={handleBackdropClick}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        // Disable portal to keep popover in map context
        disablePortal={false}
        // Keep popover mounted for smoother transitions
        keepMounted={false}
        // Disable backdrop interaction blocking for hover mode
        slotProps={{
          paper: {
            sx: {
              pointerEvents: 'auto',
            },
          },
          // Make backdrop invisible but still clickable in click mode
          root: {
            // In hover mode, don't block interaction with map
            style: mode === 'hover' ? { pointerEvents: 'none' } : undefined,
          },
        }}
        // Disable auto-focus to prevent scroll jumps
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
      >
        <Box
          data-test-id="truck-popover-content"
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
          onClick={handlePopoverClick}
          sx={{ pointerEvents: 'auto' }}
        >
          {mode === 'hover' ? (
            <TruckHoverPopoverContent truckLocation={activeTruck.truckLocation} />
          ) : (
            <TruckOnClickPopoverContent
              truckLocation={activeTruck.truckLocation}
              onViewRoute={onViewRoute}
            />
          )}
        </Box>
      </Popover>
    );
  },
);
